import React, { useEffect, useState } from "react";
import { Routes, Route } from "react-router-dom";
import PortalLayout from "./PortalLayout";
import { StatCard, Panel, Badge, EmptyState, SkeletonCards } from "./BuyerPortal";
import SiteVisitsPanel from "@/components/SiteVisitsPanel";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    LayoutDashboard, Package, ClipboardCheck, BarChart3, Tag, Users,
    IndianRupee, Home as HomeIcon, Settings, TrendingUp, Plus, CalendarCheck,
} from "lucide-react";

const NAV = [
    { to: "/portal/builder",             l: "Dashboard",       icon: LayoutDashboard, end: true },
    { to: "/portal/builder/inventory",   l: "My Inventory",    icon: Package },
    { to: "/portal/builder/site-visits", l: "Site Visits",     icon: CalendarCheck },
    { to: "/portal/builder/bookings",    l: "Bookings",        icon: ClipboardCheck },
    { to: "/portal/builder/analytics",   l: "Sales Analytics", icon: BarChart3 },
    { to: "/portal/builder/offers",      l: "Offers & Promos", icon: Tag },
    { to: "/portal/builder/partners",    l: "Channel Partners",icon: Users },
    { to: "/portal/builder/settings",    l: "Settings",        icon: Settings },
];

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

export default function BuilderPortal() {
    return (
        <Routes>
            <Route element={<PortalLayout role="builder" brand="Builder Portal" navItems={NAV}/>}>
                <Route index element={<Dashboard/>} />
                <Route path="inventory" element={<Inventory/>} />
                <Route path="site-visits" element={<SiteVisitsPanel endpoint="/builder/site-visits" title="Site Visits at My Projects" scopeLabel="Only visits scheduled at properties developed by your company. Coordinate with EBG advisors for hospitality." testid="builder-site-visits"/>} />
                <Route path="bookings" element={<Bookings/>} />
                <Route path="analytics" element={<Analytics/>} />
                <Route path="offers" element={<Panel title="Offers & Promos"><EmptyState msg="Publish limited-time offers to boost partner sell-through."/></Panel>} />
                <Route path="partners" element={<Panel title="Channel Partners"><EmptyState msg="750+ active channel partners in your regional network."/></Panel>} />
                <Route path="settings" element={<Panel title="Settings"><p className="text-black/55 text-sm">Company profile settings coming soon.</p></Panel>} />
            </Route>
        </Routes>
    );
}

function Dashboard() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/builder/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);
    if (!data) return <SkeletonCards/>;
    const s = data.stats;
    return (
        <div className="space-y-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard Icon={HomeIcon}     v={s.properties}     l="Projects Listed" trend="+1 this qtr" testid="stat-projects"/>
                <StatCard Icon={Package}      v={s.units_available} l="Units Available" trend="Live inventory"/>
                <StatCard Icon={CalendarCheck} v={s.site_visits || 0} l="Site Visits"    trend="Scheduled" testid="stat-builder-visits"/>
                <StatCard Icon={IndianRupee}  v={inr(s.revenue)}   l="Revenue"          trend="+18% MoM"/>
            </div>
            <div className="grid lg:grid-cols-2 gap-6">
                <Panel title="Top Performing Projects">
                    <ul className="space-y-3">
                        {[
                            { n: "Skyline Residences", pct: 78 },
                            { n: "The Camellias",       pct: 62 },
                            { n: "Embassy One",         pct: 45 },
                        ].map((p) => (
                            <li key={p.n}>
                                <div className="flex items-center justify-between text-[13px] mb-1"><span>{p.n}</span><span className="font-bold">{p.pct}%</span></div>
                                <div className="h-1.5 bg-black/8"><div className="h-full bg-[var(--ebg-gold)]" style={{ width: p.pct + "%" }}/></div>
                            </li>
                        ))}
                    </ul>
                </Panel>
                <Panel title="Recent Bookings">
                    <ul className="divide-y divide-black/8">
                        {[
                            { p: "Skyline 1201", by: "Vikram Realty", a: 8500000 },
                            { p: "The Camellias 402", by: "Vikram Realty", a: 12500000 },
                            { p: "Embassy One 305", by: "Vikram Realty", a: 6800000 },
                        ].map((b, i) => (
                            <li key={i} className="py-2.5 flex items-center justify-between">
                                <div><div className="text-[13px] font-bold">{b.p}</div><div className="text-black/50 text-[11px]">via {b.by}</div></div>
                                <div className="text-[var(--ebg-gold)] font-bold text-[13px]">{inr(b.a)}</div>
                            </li>
                        ))}
                    </ul>
                </Panel>
            </div>
        </div>
    );
}

function Inventory() {
    const [items, setItems]     = useState([]);
    const [props, setProps]     = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm]       = useState({ property_id: "", units_total: 100, units_available: 40, price_per_sqft: 12000, offer: "" });
    const [busy, setBusy]       = useState(false);

    const load = () => api.get("/builder/inventory").then((r) => setItems(r.data.items || []));
    useEffect(() => {
        load();
        api.get("/properties").then((r) => setProps(r.data.items || [])).catch(() => {});
    }, []);

    const propTitle = (id) => props.find((p) => p.id === id)?.title || id;

    const submit = async (e) => {
        e.preventDefault();
        if (!form.property_id) return toast.error("Please select a project");
        setBusy(true);
        try {
            await api.post("/builder/inventory", {
                ...form,
                units_total: Number(form.units_total) || 0,
                units_available: Number(form.units_available) || 0,
                price_per_sqft: form.price_per_sqft ? Number(form.price_per_sqft) : null,
            });
            toast.success("Inventory saved");
            setShowForm(false);
            setForm({ property_id: "", units_total: 100, units_available: 40, price_per_sqft: 12000, offer: "" });
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    return (
        <>
            <Panel title="My Inventory" testid="builder-inventory">
                <div className="flex items-center justify-end mb-4 -mt-2">
                    <button
                        onClick={() => setShowForm(true)}
                        data-testid="builder-add-inventory-btn"
                        className="bg-black text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[var(--ebg-gold)] hover:text-black inline-flex items-center gap-2"
                    >
                        <Plus size={13}/> Add / Update Inventory
                    </button>
                </div>
                {items.length === 0 ? <EmptyState msg="Upload your first project inventory to go live for partners."/> : (
                    <table className="w-full">
                        <thead><tr className="text-left text-[10px] uppercase tracking-[0.18em] text-black/50 border-b border-black/10">
                            <th className="pb-3">Project</th><th className="pb-3">Units Total</th><th className="pb-3">Available</th><th className="pb-3">Price/sqft</th><th className="pb-3">Offer</th>
                        </tr></thead>
                        <tbody>
                            {items.map((it, i) => (
                                <tr key={i} className="border-b border-black/8 last:border-b-0" data-testid={`inventory-row-${i}`}>
                                    <td className="py-3 font-bold">{propTitle(it.property_id)}</td>
                                    <td className="py-3">{it.units_total}</td>
                                    <td className="py-3">{it.units_available}</td>
                                    <td className="py-3 tabular-nums">{it.price_per_sqft ? inr(it.price_per_sqft) : "—"}</td>
                                    <td className="py-3 text-black/60">{it.offer || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Panel>

            {showForm && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6" data-testid="inventory-form">
                    <form onSubmit={submit} className="bg-white w-full max-w-lg p-6 space-y-3">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">Add / Update</div>
                                <h3 className="font-serif text-2xl mt-1">Inventory</h3>
                            </div>
                            <button type="button" onClick={() => setShowForm(false)} className="text-black/50 hover:text-black">✕</button>
                        </div>
                        <label className="block">
                            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/55 mb-1.5">Project *</div>
                            <select data-testid="inv-project" value={form.property_id} onChange={(e) => setForm({ ...form, property_id: e.target.value })} required className="w-full border border-black/15 px-3 py-2.5 text-sm bg-white outline-none focus:border-[var(--ebg-gold)]">
                                <option value="">— Select project —</option>
                                {props.map((p) => <option key={p.id} value={p.id}>{p.title} · {p.city}</option>)}
                            </select>
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            <F l="Units Total *"    v={form.units_total}     onChange={(v) => setForm({ ...form, units_total: v })} testid="inv-total"/>
                            <F l="Units Available *" v={form.units_available} onChange={(v) => setForm({ ...form, units_available: v })} testid="inv-avail"/>
                        </div>
                        <F l="Price / sq.ft (₹)" v={form.price_per_sqft} onChange={(v) => setForm({ ...form, price_per_sqft: v })} testid="inv-price-sqft"/>
                        <F l="Offer (optional)"  v={form.offer} onChange={(v) => setForm({ ...form, offer: v })}/>
                        <button disabled={busy} data-testid="inv-submit" className="w-full bg-[var(--ebg-gold)] text-black py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-black hover:text-[var(--ebg-gold)] disabled:opacity-60">
                            {busy ? "Saving…" : "Save Inventory"}
                        </button>
                    </form>
                </div>
            )}
        </>
    );
}

function F({ l, v, onChange, testid }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/55 mb-1.5">{l}</div>
            <input data-testid={testid} value={v ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-black/15 px-3 py-2.5 text-sm outline-none focus:border-[var(--ebg-gold)]"/>
        </label>
    );
}

function Bookings() {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/builder/bookings").then((r) => setItems(r.data.items || [])); }, []);
    return (
        <Panel title="Bookings" testid="builder-bookings">
            {items.length === 0 ? <EmptyState msg="No bookings yet."/> : (
                <table className="w-full">
                    <thead><tr className="text-left text-[10px] uppercase tracking-[0.18em] text-black/50 border-b border-black/10">
                        <th className="pb-3">Property</th><th className="pb-3">Buyer</th><th className="pb-3">Amount</th><th className="pb-3">Status</th>
                    </tr></thead>
                    <tbody>
                        {items.map((b) => (
                            <tr key={b.id} className="border-b border-black/8 last:border-b-0">
                                <td className="py-3">{b.property_title}</td>
                                <td className="py-3 text-black/70">{b.buyer_name}</td>
                                <td className="py-3 font-bold text-[var(--ebg-gold)]">{inr(b.amount)}</td>
                                <td className="py-3"><Badge tone="green">{b.status}</Badge></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </Panel>
    );
}

function Analytics() {
    return (
        <div className="space-y-6">
            <div className="grid md:grid-cols-3 gap-4">
                <StatCard Icon={TrendingUp}  v="₹4.28 Cr" l="This Quarter Revenue" trend="+22% YoY"/>
                <StatCard Icon={Users}       v="82"       l="Partner Enquiries"    trend="+18 this wk"/>
                <StatCard Icon={ClipboardCheck} v="34"    l="Site Visits"           trend="Booked"/>
            </div>
            <Panel title="Sales Trend"><div className="h-40 flex items-end gap-3">
                {[38, 46, 52, 42, 68, 74, 82, 90].map((h, i) => (
                    <div key={i} className="flex-1 bg-[var(--ebg-gold)]/40 hover:bg-[var(--ebg-gold)] transition-colors" style={{ height: h + "%" }} title={`Month ${i+1}`}/>
                ))}
            </div></Panel>
        </div>
    );
}
